import React, { useCallback, useEffect, useState } from 'react';
import { Keyboard } from 'react-native';
import { useNavigation, useNavigationState } from '@react-navigation/native';

import AppTabBar from '../../design/components/AppTabBar';
import {
	APP_TAB,
	APP_TABS,
	TAB_BAR_HIDDEN_ROUTES,
	getActiveTab,
} from '../../scenes/driver/driverRoutes';
import { useUserContext } from '../../../common/utils/context';
import { useDriverShell } from '../../scenes/driver/DriverDayProvider';
import { MY_DAY_MODE } from '../../scenes/myDay/useMyDayMode';
import { setFieldTabBarHeight } from './fieldTabBarHeight';
import goToShellRoute, { getMainStackState } from './goToShellRoute';
import useVisibleTabs from './useVisibleTabs';

const EMPTY_STACK = { key: undefined, routes: [] };

/**
 * Bottom bar of the redesigned app, rendered once below the main stack so it
 * shows on every screen (restyled or not). Tabs follow the user's role
 * (DriverDayProvider tabBarVariant); the active tab follows the stack, so a
 * phase's pushed pages keep their phase highlighted. Tapping a tab returns to
 * that tab's screen if it is in the stack, otherwise starts over from Home.
 */
const MainTabBar = () => {
	const navigation = useNavigation();
	const { isDriver } = useUserContext();
	const { mode } = useDriverShell();
	const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

	// This renders inside the drawer's MainStack screen, so the navigation
	// state here is the drawer's; dig out the stack.
	const stack = useNavigationState((state) => {
		const stackState = getMainStackState(state);
		return stackState?.routes ? stackState : EMPTY_STACK;
	});

	useEffect(() => {
		const showSubscription = Keyboard.addListener('keyboardDidShow', () => setIsKeyboardOpen(true));
		const hideSubscription = Keyboard.addListener('keyboardDidHide', () => setIsKeyboardOpen(false));

		return () => {
			showSubscription.remove();
			hideSubscription.remove();
		};
	}, []);

	const visibleTabs = useVisibleTabs();
	const currentRouteName = stack.routes[stack.index ?? stack.routes.length - 1]?.name;
	const activeTab = getActiveTab(stack.routes, visibleTabs);

	const goToTab = useCallback((tab) => {
		goToShellRoute(navigation, stack, APP_TABS[tab].route);
	}, [navigation, stack]);

	const onBarLayout = useCallback((event) => {
		setFieldTabBarHeight(event.nativeEvent.layout.height);
	}, []);

	// A bar with only Home would be noise (e.g. roles without the run list).
	const isVisible = visibleTabs.length > 1 &&
		!isKeyboardOpen &&
		!TAB_BAR_HIDDEN_ROUTES.has(currentRouteName);

	// Root-level overlays (sync banner) offset themselves by the published
	// height; reset it whenever the bar hides or unmounts.
	useEffect(() => {
		if (!isVisible) {
			setFieldTabBarHeight(0);
		}
	}, [isVisible]);
	useEffect(() => () => setFieldTabBarHeight(0), []);

	if (!isVisible) {
		return null;
	}

	const tabs = visibleTabs.map(tab => ({
		key: tab,
		label: tab === APP_TAB.ALL_RUNS && isDriver && mode !== MY_DAY_MODE.PICKER ? 'My runs' : APP_TABS[tab].label,
		icon: APP_TABS[tab].icon,
		active: tab === activeTab,
		onPress: () => goToTab(tab),
	}));

	return <AppTabBar tabs={tabs} onLayout={onBarLayout} />;
};

export default MainTabBar;
